import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  Target, 
  Zap, 
  Users, 
  Award,
  CheckCircle,
  ArrowRight,
  Heart,
  Eye,
  Lightbulb,
  Linkedin
} from 'lucide-react';
import { Button } from '../components/ui/button';

const TEAM_IMAGE = "https://images.unsplash.com/photo-1603516270950-26e4f5004ffd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjBzYWZldHklMjBoZWxtZXQlMjB2ZXN0JTIwZXF1aXBtZW50fGVufDB8fHx8MTc3MTE1NDU4Nnww&ixlib=rb-4.1.0&q=85";

// Leadership Team
const leadershipTeam = [
  {
    name: 'Dervon J Gaskins Sr',
    role: 'Chief Operating Officer (COO)',
    image: 'https://customer-assets.emergentagent.com/job_f7db9eb5-303b-40e1-ba38-b76b077cd7ba/artifacts/01lhbbti_6977.jpg',
    linkedin: 'https://www.linkedin.com/in/dervon-j-g-45198441/',
    description: 'Overseeing daily operations and ensuring service excellence.'
  },
  {
    name: 'Catherine Lasso, MBA',
    role: 'Chief Administrative Officer (CAO)',
    image: 'https://customer-assets.emergentagent.com/job_f7db9eb5-303b-40e1-ba38-b76b077cd7ba/artifacts/tv6of2v4_IMG_0200.jpeg',
    linkedin: 'https://www.linkedin.com/in/catherine-lasso-mba-94125915b/',
    description: 'Managing administrative functions and organizational development.'
  },
  {
    name: 'Pankaj Chaturvedi',
    role: 'Chief Executive Officer (CEO)',
    image: 'https://customer-assets.emergentagent.com/job_f7db9eb5-303b-40e1-ba38-b76b077cd7ba/artifacts/o3s40z7m_IMG_3515.JPG',
    linkedin: 'https://www.linkedin.com/in/pankajchaturvedi/',
    description: 'Leading USG\'s strategic vision and growth initiatives across the nation.'
  }
];

const values = [
  {
    icon: Shield,
    title: 'Safety First',
    description: 'Every decision we make prioritizes the safety of workers, the public, and underground infrastructure.'
  },
  {
    icon: Target,
    title: 'Precision',
    description: 'We utilize the most advanced technology and rigorous methodologies to deliver accurate results.'
  },
  {
    icon: Zap,
    title: 'Responsiveness',
    description: 'Fast response times and 24/7 emergency availability keep your projects on schedule.'
  },
  {
    icon: Users,
    title: 'Partnership',
    description: 'We work as an extension of your team, understanding your unique project requirements.'
  },
];

const certifications = [
  'OSHA 10/30 Certified',
  'CGA Best Practices Trained',
  'State Licensed Locators',
  'GPR Certified Technicians',
  'GIS Mapping Specialists',
  'DOT Compliance Trained'
];

export default function AboutPage() {
  return (
    <div className="bg-skytec-bg pt-20">
      {/* Hero Section */}
      <section className="py-24 relative overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 scan-grid opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="text-skytec-secondary font-mono text-sm tracking-widest uppercase">About USG</span>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold text-skytec-fg mt-4 mb-6 uppercase tracking-tight">
                PROTECTING <span className="neon-text">INFRASTRUCTURE</span>
              </h1>
              <p className="text-skytec-fg/70 text-lg leading-relaxed mb-8">
                Utility Safe Guarding (USG) is a leading underground utility locating company dedicated to preventing 
                damage to vital infrastructure and ensuring the safety of workers and communities 
                across the United States.
              </p>
              <p className="text-skytec-fg/60 leading-relaxed">
                With over 15 years of experience and more than 50,000 projects completed, we've 
                built our reputation on precision, reliability, and an unwavering commitment to 
                safety. Our team of certified technicians uses state-of-the-art equipment to 
                deliver accurate utility locating services for projects of all sizes.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden neon-border">
                <img 
                  src={TEAM_IMAGE}
                  alt="USG Team"
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-skytec-bg/60 to-transparent" />
              </div>
              
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -left-6 glass-card rounded-xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-skytec-primary-dim rounded-full flex items-center justify-center">
                    <Award className="w-6 h-6 text-skytec-primary" />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-skytec-fg">Industry Leader</div>
                    <div className="text-skytec-fg/60 text-sm">15+ Years Excellence</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-skytec-surface border-y border-skytec-border" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Our Mission</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mt-4 uppercase tracking-tight">
              WHAT DRIVES US FORWARD
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
              className="text-center p-8 bg-skytec-bg rounded-2xl border border-skytec-border"
            >
              <div className="w-16 h-16 bg-skytec-primary-dim rounded-full flex items-center justify-center mx-auto mb-6">
                <Eye className="w-8 h-8 text-skytec-primary" />
              </div>
              <h3 className="font-heading text-xl font-bold text-skytec-fg mb-4 uppercase">Our Vision</h3>
              <p className="text-skytec-fg/60">
                To be the most trusted name in underground utility locating, setting the 
                industry standard for safety, accuracy, and customer service.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center p-8 bg-skytec-bg rounded-2xl border border-skytec-border"
            >
              <div className="w-16 h-16 bg-skytec-secondary-dim rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="w-8 h-8 text-skytec-secondary" />
              </div>
              <h3 className="font-heading text-xl font-bold text-skytec-fg mb-4 uppercase">Our Mission</h3>
              <p className="text-skytec-fg/60">
                To protect lives, infrastructure, and communities by providing precise 
                underground utility locating services with uncompromising safety standards.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center p-8 bg-skytec-bg rounded-2xl border border-skytec-border"
            >
              <div className="w-16 h-16 bg-skytec-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lightbulb className="w-8 h-8 text-skytec-accent" />
              </div>
              <h3 className="font-heading text-xl font-bold text-skytec-fg mb-4 uppercase">Our Approach</h3>
              <p className="text-skytec-fg/60">
                Combining cutting-edge technology with expert knowledge to deliver 
                comprehensive utility locating solutions tailored to each project.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Our Values</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mt-4 uppercase tracking-tight">
              THE PRINCIPLES WE LIVE BY
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-skytec-surface rounded-xl border border-skytec-border hover:border-skytec-primary/30 transition-colors group"
              >
                <div className="w-12 h-12 bg-skytec-primary-dim rounded-lg flex items-center justify-center mb-4 group-hover:bg-skytec-primary/20 transition-colors">
                  <value.icon className="w-6 h-6 text-skytec-primary" />
                </div>
                <h3 className="font-heading font-bold text-skytec-fg mb-2">{value.title}</h3>
                <p className="text-skytec-fg/60 text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team Section */}
      <section className="py-24 bg-skytec-surface border-y border-skytec-border" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Leadership</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mt-4 uppercase tracking-tight">
              MEET OUR TEAM
            </h2>
            <p className="text-skytec-fg/60 mt-4 max-w-2xl mx-auto">
              Our experienced leadership team brings decades of combined expertise in utility locating, operations, and business management.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {leadershipTeam.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-skytec-bg rounded-2xl border border-skytec-border overflow-hidden group hover:border-skytec-primary/30 transition-all shadow-lg"
              >
                <div className="relative bg-gray-100 dark:bg-gray-800">
                  <img 
                    src={member.image}
                    alt={member.name}
                    className="w-full h-72 object-cover"
                    style={{ 
                      objectPosition: member.role.includes('CAO') ? 'center 20%' : 
                                      member.role.includes('COO') ? 'center 25%' : 'center center' 
                    }}
                  />
                </div>
                <div className="p-6 bg-skytec-bg">
                  <h3 className="font-heading text-xl font-bold text-skytec-fg mb-1">{member.name}</h3>
                  <p className="text-skytec-secondary font-semibold text-sm mb-3">{member.role}</p>
                  <p className="text-skytec-fg/70 text-sm mb-4">{member.description}</p>
                  <a 
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-skytec-primary hover:text-skytec-secondary transition-colors font-medium"
                    data-testid={`linkedin-${member.role.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  >
                    <Linkedin className="w-5 h-5 mr-2" />
                    <span className="text-sm">Connect on LinkedIn</span>
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-24 bg-skytec-surface border-y border-skytec-border" data-testid="certifications-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Certifications</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mt-4 mb-6 uppercase tracking-tight">
                CERTIFIED PROFESSIONALS
              </h2>
              <p className="text-skytec-fg/70 mb-8">
                Our technicians undergo rigorous training and maintain industry-leading 
                certifications to ensure the highest standards of service and safety.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {certifications.map((cert, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-skytec-accent mr-3 flex-shrink-0" />
                    <span className="text-skytec-fg/80">{cert}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="relative rounded-2xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1631583087046-13c813d34e90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHxjb25zdHJ1Y3Rpb24lMjBzYWZldHklMjBoZWxtZXQlMjB2ZXN0JTIwZXF1aXBtZW50fGVufDB8fHx8MTc3MTE1NDU4Nnww&ixlib=rb-4.1.0&q=85"
                  alt="Certified Technician"
                  className="w-full aspect-video object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-skytec-surface border-t border-skytec-border" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mb-6 uppercase">
              READY TO WORK WITH THE BEST?
            </h2>
            <p className="text-skytec-fg/60 max-w-2xl mx-auto mb-10">
              Join the thousands of contractors, municipalities, and businesses 
              that trust USG for their utility locating needs.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-skytec-primary text-skytec-bg hover:bg-skytec-primary/90 font-heading font-bold"
                  data-testid="about-contact-btn"
                >
                  GET STARTED TODAY
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/services">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-skytec-fg/30 text-skytec-fg hover:bg-skytec-fg/10 font-heading"
                >
                  EXPLORE SERVICES
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
